import { solarShortProductName } from '@payaca/helpers/solarProductName';
import {
  AnchorButton,
  Badge,
  Button,
  ContextMenuDropdown,
  Divider,
  H4,
  RawInput,
  Select,
  Text,
  Tooltip,
} from '@payaca/ambrosia';
import { Link } from 'react-router-dom';
import { estimateSettingsPathForCategory } from '@/ui/components/companySettings/estimateSettingsRoutes';
import {
  applySolarElectricalChange,
  type SolarElectricalChange,
} from '@payaca/helpers/solarElectricalChange';
import UntitledIcon from '@payaca/untitled-icons';
import {
  inspectSolarElectricalTopology,
  solarInverterAllocationSummary,
  type SolarElectricalTopology,
} from '@payaca/helpers/solarElectricalTopology';
import { type SolarRecommendedApplianceFieldsFragment } from '@/gql/graphql';
import {
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { QueryClientContext } from '@tanstack/react-query';
import meKeys from '@/api/queries/me/keyFactory';
import { type MyRestProfile } from '@/api/rest/users/useGetMyRestProfile';
import { solarSavingsRegion } from '@payaca/helpers/solarSavingsConstants';
import { type SolarDiagramRegion } from '@payaca/helpers/solarSingleLineDiagram';
import ElectricalDiagram from './ElectricalDiagram';
import { compassLabel } from './roofLabels';
import {
  autoStringSolarTopology,
  suggestSolarInverterFleet,
  type SolarAutoStringInput,
  type SolarAutoStringResult,
  type SolarInverterFleetOption,
} from '@payaca/helpers/solarAutoString';
import {
  checkSolarTopologyString,
  type SolarStringCheck,
  type SolarStringSpecKey,
} from '@payaca/helpers/solarStringCheck';
import {
  isSolarOptimiserInverter,
  nextSolarInverterName,
  SOLAR_OPTIMISER_STRING_REASON,
  solarInverterMpptCount,
  solarMaxStringsPerMppt,
  solarTrackerDeclarations,
  solarTrackerLabel,
  solarVoltageMaxPanelsPerString,
} from '@payaca/helpers/solarInverterUnits';
import { SOLAR_STRING_PALETTE } from '@payaca/components/solarRoofView/SolarRoofView';
import { InspectorFooterContext } from './inspectorFooter';

type Props = {
  topology: SolarElectricalTopology;
  panels: SolarAutoStringInput['panels'];
  /** Roofs in picker order; azimuth names each string's roof. */
  faces: { id: number; azimuthDegrees: number }[];
  excludedFaceIds: number[];
  selectedPanelIds: string[];
  appliances: SolarRecommendedApplianceFieldsFragment[];
  panelAppliances: SolarRecommendedApplianceFieldsFragment[];
  /** Must apply successive calls on top of each other (Auto-string sends several). */
  onChange: (change: SolarElectricalChange) => void;
  layoutChangeDisabledReason?: string;
  onHighlightPanels: (ids: string[]) => void;
  onHoverPanels: (ids: string[]) => void;
  colourKeys: ReadonlyMap<string, number>;
  /** The one selected string, owned by the designer so the map can set it. */
  selectedStringId: string | null;
  onSelectedStringIdChange: (id: string | null) => void;
  /** Diagram labelling and breaker sizing; defaults to the signed-in account's region. */
  region?: SolarDiagramRegion;
  /** The design's selected battery, drawn on the diagram. */
  battery?: SolarRecommendedApplianceFieldsFragment | null;
  /** Site design low (°C) from the design's location. */
  siteMinTemperatureC?: number | null;
  /**
   * The Electrical tab's Auto-string just set allocation up: string the
   * design once on mount, then report it handled.
   */
  autoStringOnMount?: boolean;
  onAutoStringHandled?: () => void;
};

const plural = (count: number, one: string, many: string) =>
  `${count} ${count === 1 ? one : many}`;

const NO_UNITS_TOOLTIP = 'Add an inverter unit first';
const ADD_UNIT_ROW_ID = 'solar-add-inverter-row';

/** Palette colour for a string's colour key; the roof uses the same key. */
export const stringSwatch = (key: number) =>
  SOLAR_STRING_PALETTE[key % SOLAR_STRING_PALETTE.length];

/** The helpers' unit rules, shared with the GraphQL API (solarInverterUnits.ts). */
export const nextInverterName = nextSolarInverterName;
export const isOptimiserInverter = isSolarOptimiserInverter;
export const trackerDeclarations = solarTrackerDeclarations;
const mpptCountOf = solarInverterMpptCount;

/** First "<prefix> n" not in use. */
const nextKey = (prefix: string, used: ReadonlySet<string>) => {
  let n = 1;
  while (used.has(`${prefix} ${n}`)) n++;
  return `${prefix} ${n}`;
};

/** A1, A2… after the first unit's letter, for a manually added string. */
export const nextStringName = (
  unitNames: readonly string[],
  stringNames: readonly string[]
) => {
  const letter = /^Inverter ([A-Z]+)$/.exec(unitNames[0] ?? '')?.[1] ?? 'A';
  return nextKey(letter, new Set(stringNames)).replace(' ', '');
};

type ConnectionStatus = 'Needs input' | 'Review' | 'Optimiser';
const BADGE: Record<
  ConnectionStatus | SolarStringCheck['status'],
  { label: string; colour: 'yellow' | 'orange' | 'green' | 'gray' }
> = {
  'Needs input': { label: 'Needs input', colour: 'yellow' },
  Review: { label: 'Review', colour: 'orange' },
  // The optimisers fix the string voltage: no window or 1.25 × Isc check.
  Optimiser: { label: 'Optimiser system', colour: 'gray' },
  ok: { label: 'OK', colour: 'green' },
  check: { label: 'Check', colour: 'yellow' },
  unknown: { label: 'Not checked', colour: 'gray' },
};

/** Connectivity editor only. All operations use stable IDs and the shared reducer. */
export const ElectricalAllocationPanel = ({
  topology,
  panels,
  faces,
  excludedFaceIds,
  selectedPanelIds,
  appliances,
  panelAppliances,
  onChange,
  layoutChangeDisabledReason,
  onHighlightPanels,
  onHoverPanels,
  colourKeys,
  selectedStringId,
  onSelectedStringIdChange,
  region,
  battery,
  siteMinTemperatureC = null,
  autoStringOnMount = false,
  onAutoStringHandled,
}: Props) => {
  // The profile is loaded before any route renders; read it without subscribing.
  const queryClient = useContext(QueryClientContext);
  const diagramRegion =
    region ??
    solarSavingsRegion(
      queryClient?.getQueryData<MyRestProfile>(meKeys.restProfile())
        ?.accounts[0]?.region
    );
  const stringColours = useMemo(
    () =>
      new Map(
        topology.strings.map((string) => [
          string.id,
          stringSwatch(colourKeys.get(string.id) ?? 0),
        ])
      ),
    [topology.strings, colourKeys]
  );
  const footer = useContext(InspectorFooterContext);
  const [renamingString, setRenamingString] = useState(false);
  const [stringName, setStringName] = useState('');
  const [renamingUnit, setRenamingUnit] = useState<{
    id: string;
    value: string;
  } | null>(null);
  const [connectionDraft, setConnectionDraft] = useState<{
    stringId: string;
    instanceId: string;
    mpptKey: string;
  } | null>(null);
  const [autoSummary, setAutoSummary] = useState<
    SolarAutoStringResult['summary'] | null
  >(null);
  const faceIds = faces.map((face) => face.id);
  useEffect(() => () => onHoverPanels([]), [onHoverPanels]);
  /**
   * Units saved before trackers came from the catalogue: declare them once,
   * through onChange so the draft autosaves.
   */
  const backfilled = useRef(new Set<string>());
  useEffect(() => {
    for (const instance of topology.inverterInstances) {
      if (instance.trackers.length || backfilled.current.has(instance.id))
        continue;
      const applianceId = topology.equipmentSelections.find(
        (item) => item.id === instance.equipmentSelectionId
      )?.applianceId;
      const spec = appliances.find((item) => item.appliance.id === applianceId)
        ?.appliance.productSpec;
      const changes = trackerDeclarations(instance.id, spec);
      if (!changes.length) continue;
      backfilled.current.add(instance.id);
      for (const change of changes) onChange(change);
    }
  }, [topology, appliances, onChange]);
  const hasUnits = topology.inverterInstances.length > 0;
  const eligible = [
    ...new Map(
      appliances
        .filter(
          (choice) =>
            choice.appliance.productSpec.category === 'INVERTER' &&
            choice.appliance.productSpec.inverterTopology !== 'MICRO'
        )
        .map((choice) => [choice.appliance.id, choice])
    ).values(),
  ];
  const [pickedApplianceId, setPickedApplianceId] = useState<string | null>(
    null
  );
  // The product already in use first, so adding a unit matches the fleet.
  const inUseApplianceId = topology.inverterInstances
    .map(
      (unit) =>
        topology.equipmentSelections.find(
          (item) => item.id === unit.equipmentSelectionId
        )?.applianceId
    )
    .find((id) => eligible.some((item) => item.appliance.id === id));
  const applianceId =
    eligible.find((item) => item.appliance.id === pickedApplianceId)?.appliance
      .id ??
    inUseApplianceId ??
    eligible[0]?.appliance.id ??
    '';
  const [changingUnitId, setChangingUnitId] = useState<string | null>(null);
  const [movingStringId, setMovingStringId] = useState<string | null>(null);

  const review = inspectSolarElectricalTopology(
    topology,
    panels,
    faceIds,
    excludedFaceIds
  );
  /** Face IDs are not array indexes; display the same 1-based roof numbers as the roof picker. */
  const roofNumber = (faceId: number) => {
    const index = faceIds.indexOf(faceId);
    return index >= 0 ? index + 1 : faceId;
  };
  const panelById = new Map(panels.map((panel) => [panel.id, panel]));
  const selectionById = new Map(
    topology.equipmentSelections.map((item) => [item.id, item])
  );
  /** Saved snapshot first, then the live catalogue fragment. Any finite number. */
  const specNumber = (
    selectionId: string | null | undefined,
    catalogue: SolarRecommendedApplianceFieldsFragment[],
    key: SolarStringSpecKey | 'panelCapacityW',
    base: SolarElectricalTopology = topology
  ): number | null => {
    const selection = base.equipmentSelections.find(
      (item) => item.id === selectionId
    );
    const snapshot = selection?.specSnapshot?.specs;
    const saved =
      snapshot && typeof snapshot === 'object' && !Array.isArray(snapshot)
        ? snapshot[key]
        : null;
    if (typeof saved === 'number' && Number.isFinite(saved)) return saved;
    const live = catalogue.find(
      (item) => item.appliance.id === selection?.applianceId
    )?.appliance.productSpec?.[key];
    return typeof live === 'number' && Number.isFinite(live) ? live : null;
  };

  const unitSpecOf = (selectionId: string | null | undefined) => {
    const applianceId = selectionById.get(selectionId ?? '')?.applianceId;
    return appliances.find((item) => item.appliance.id === applianceId)
      ?.appliance.productSpec;
  };
  /** Included DC on active roofs, from saved or catalogue panel ratings. */
  const totalDcKw = panels.reduce((sum, panel) => {
    if (!panel.included || excludedFaceIds.includes(panel.faceId)) return sum;
    return (
      sum +
      (specNumber(
        panel.equipmentSelectionId,
        panelAppliances,
        'panelCapacityW'
      ) ?? 0) /
        1000
    );
  }, 0);
  const fleet = suggestSolarInverterFleet({
    totalDcKw,
    products: eligible.map(({ appliance }) => ({
      applianceId: appliance.id,
      name: solarShortProductName(appliance.productSpec.name),
      acOutputKw: appliance.productSpec.acOutputKw ?? null,
      mpptCount: mpptCountOf(appliance.productSpec),
      phases: appliance.productSpec.phases ?? null,
    })),
    existingUnits: topology.inverterInstances.map((unit) => ({
      applianceId:
        selectionById.get(unit.equipmentSelectionId)?.applianceId ?? null,
    })),
    // A three-phase unit in the design means a three-phase supply.
    supplyPhases: topology.inverterInstances.some(
      (unit) => unitSpecOf(unit.equipmentSelectionId)?.phases === 3
    )
      ? 3
      : null,
  });

  const selectedString = topology.strings.find(
    (string) => string.id === selectedStringId
  );
  const selectedMembership = new Set(selectedString?.panelIds);
  const addingIds = selectedPanelIds.filter(
    (id) => !selectedMembership.has(id)
  );
  const removingIds = selectedPanelIds.filter((id) =>
    selectedMembership.has(id)
  );
  const movingCount = topology.strings
    .filter((item) => item.id !== selectedStringId)
    .reduce(
      (count, item) =>
        count + item.panelIds.filter((id) => addingIds.includes(id)).length,
      0
    );
  const connectionInstanceId =
    connectionDraft?.stringId === selectedStringId
      ? connectionDraft.instanceId
      : (selectedString?.inverterInstanceId ?? '');
  const connectionMpptKey =
    connectionDraft?.stringId === selectedStringId
      ? connectionDraft.mpptKey
      : (selectedString?.mpptKey ?? '');
  const connectionInstance = topology.inverterInstances.find(
    (item) => item.id === connectionInstanceId
  );

  const connectionTracker = connectionInstance?.trackers.find(
    (item) => item.key === connectionMpptKey
  );
  const takenInputs = new Set(
    topology.strings
      .filter(
        (item) =>
          item.id !== selectedStringId &&
          item.inverterInstanceId === connectionInstanceId
      )
      .map((item) => item.inputKey)
  );
  const freeInputKey = (instanceId: string, mpptKey: string) =>
    topology.inverterInstances
      .find((item) => item.id === instanceId)
      ?.trackers.find((item) => item.key === mpptKey)
      ?.inputs.find(
        (input) =>
          !topology.strings.some(
            (item) =>
              item.id !== selectedStringId &&
              item.inverterInstanceId === instanceId &&
              item.inputKey === input.key
          )
      )?.key;
  const needsAnotherInput =
    !!selectedString &&
    !!connectionTracker &&
    connectionTracker.inputs.every((input) => takenInputs.has(input.key));
  const declaredInputKeys = new Set(
    connectionInstance?.trackers.flatMap((tracker) =>
      tracker.inputs.map((input) => input.key)
    )
  );
  const extraInputKey = nextKey('DC', declaredInputKeys);

  const selectString = (id: string | null, panelIds: string[] = []) => {
    onSelectedStringIdChange(id);
    setRenamingString(false);
    onHighlightPanels(panelIds);
  };

  const countryCode = diagramRegion === 'us' ? 'US' : 'GB';
  const activePanelSelections = [
    ...new Set(
      panels
        .filter(
          (item) => item.included && !excludedFaceIds.includes(item.faceId)
        )
        .map((item) => item.equipmentSelectionId)
    ),
  ];
  /** Highest Isc on the design's active panels, for the per-MPPT allowance. */
  const designPanelIscA = (base: SolarElectricalTopology) =>
    Math.max(
      0,
      ...activePanelSelections.map(
        (selectionId) =>
          specNumber(
            selectionId,
            panelAppliances,
            'shortCircuitCurrentA',
            base
          ) ?? 0
      )
    ) || null;

  /**
   * Longest string the cold-weather voltage allows (solarVoltageMaxPanelsPerString):
   * each panel's own coefficient, 600 V for a unit without a limit, capped at 13.
   */
  const voltageMaxPanels = (base: SolarElectricalTopology) =>
    solarVoltageMaxPanelsPerString({
      panelVocsV: activePanelSelections.map((selectionId) =>
        specNumber(selectionId, panelAppliances, 'openCircuitVoltageV', base)
      ),
      panelVocTemperatureCoefficientsPctPerC: activePanelSelections.map(
        (selectionId) =>
          specNumber(
            selectionId,
            panelAppliances,
            'vocTemperatureCoefficientPctPerC',
            base
          )
      ),
      inverterMaxInputVoltagesV: base.inverterInstances.map((unit) =>
        specNumber(
          unit.equipmentSelectionId,
          appliances,
          'maxInputVoltageV',
          base
        )
      ),
      siteMinTemperatureC,
      countryCode,
    });

  const runAutoString = (base: SolarElectricalTopology = topology) => {
    const maxPanelsPerString = voltageMaxPanels(base);
    const panelIscA = designPanelIscA(base);
    const result = autoStringSolarTopology({
      ...(maxPanelsPerString != null ? { rules: { maxPanelsPerString } } : {}),
      topology: base,
      panels,
      faces,
      excludedFaceIds,
      maxStringsPerMppt: (instance) => {
        const applianceId = base.equipmentSelections.find(
          (item) => item.id === instance.equipmentSelectionId
        )?.applianceId;
        const spec = appliances.find(
          (item) => item.appliance.id === applianceId
        )?.appliance.productSpec;
        return solarMaxStringsPerMppt({
          spec: spec && {
            ...spec,
            maxMpptIscA: specNumber(
              instance.equipmentSelectionId,
              appliances,
              'maxMpptIscA',
              base
            ),
          },
          panelIscA,
        });
      },
    });
    for (const change of result.changes) onChange(change);
    setAutoSummary(result.summary);
    selectString(null);
  };

  // One Auto-string from the Electrical tab: allocation, then strings.
  const autoStrungOnMount = useRef(false);
  useEffect(() => {
    if (!autoStringOnMount || autoStrungOnMount.current) return;
    autoStrungOnMount.current = true;
    runAutoString();
    onAutoStringHandled?.();
    // Once, on the mount that follows the tab's Auto-string.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStringOnMount]);

  /** Adds the unit with its catalogue trackers; the first unit strings the design. */
  /** Adds units with automatic names and catalogue inputs, then strings when useful. */
  const addUnits = (productId: string, count = 1, restring = false) => {
    const spec = eligible.find((item) => item.appliance.id === productId)
      ?.appliance.productSpec;
    let next = topology;
    for (let unit = 0; unit < count; unit++) {
      const id = crypto.randomUUID();
      const changes: SolarElectricalChange[] = [
        {
          type: 'addInverter',
          id,
          selectionId: crypto.randomUUID(),
          applianceId: productId,
          name: nextInverterName(
            next.inverterInstances.map((item) => item.name)
          ),
        },
        ...trackerDeclarations(id, spec),
      ];
      for (const change of changes) {
        onChange(change);
        next = applySolarElectricalChange(next, change).topology;
      }
    }
    // The first unit strings the design; units added to cover a shortfall restring it.
    const shortfall = (autoSummary?.unassignedPanels ?? 0) > 0;
    if (
      (restring ||
        topology.strings.every((item) => !item.panelIds.length) ||
        shortfall) &&
      next.inverterInstances.some((unit) => unit.trackers.length)
    )
      runAutoString(next);
  };
  const addUnit = () => addUnits(applianceId);

  /** "Roof 1 South", or "2 roofs" when a string spans roofs. */
  const stringRoof = (panelIds: string[]) => {
    const roofs = [
      ...new Set(
        panelIds.flatMap((id) => {
          const panel = panelById.get(id);
          return panel ? [panel.faceId] : [];
        })
      ),
    ];
    const face = faces.find((item) => item.id === roofs[0]);
    if (roofs.length > 1) return `${roofs.length} roofs`;
    return face
      ? `Roof ${roofNumber(face.id)} ${compassLabel(face.azimuthDegrees)}`
      : '';
  };

  const stringBadge = (string: SolarElectricalTopology['strings'][number]) => {
    if (string.incompleteReason) return { ...BADGE.Review, reasons: [] };
    if (!string.inverterInstanceId || !string.mpptKey || !string.inputKey)
      return { ...BADGE['Needs input'], reasons: [] };
    const instance = topology.inverterInstances.find(
      (item) => item.id === string.inverterInstanceId
    );
    if (isOptimiserInverter(unitSpecOf(instance?.equipmentSelectionId)))
      return { ...BADGE.Optimiser, reasons: [SOLAR_OPTIMISER_STRING_REASON] };
    // Active panels only, the worst panel spec, strings sharing the input and MPPT.
    const check = checkSolarTopologyString({
      string,
      topology,
      panels,
      excludedFaceIds,
      specOf: (selectionId, key) =>
        specNumber(
          selectionId,
          selectionId === instance?.equipmentSelectionId
            ? appliances
            : panelAppliances,
          key
        ),
      siteMinTemperatureC,
      countryCode,
    });
    return { ...BADGE[check.status], reasons: check.reasons };
  };

  /**
   * Another input on a product with no MPPT count: an optimiser unit takes
   * it on its input stage, any other unit as one more assumed tracker.
   */
  const addAnotherInput = (
    instance: SolarElectricalTopology['inverterInstances'][number],
    spec: ReturnType<typeof unitSpecOf>
  ) => {
    const inputKey = nextKey(
      'DC',
      new Set(
        instance.trackers.flatMap((item) =>
          item.inputs.map((input) => input.key)
        )
      )
    );
    // "Input 1" first (the assumed tracker), then "Input 2"…
    const mpptKey =
      isOptimiserInverter(spec) && instance.trackers[0]
        ? instance.trackers[0].key
        : nextKey('Input', new Set(instance.trackers.map((item) => item.key)));
    onChange({
      type: 'declareInput',
      inverterInstanceId: instance.id,
      mpptKey,
      inputKey,
    });
  };

  const unitCard = (
    instance: SolarElectricalTopology['inverterInstances'][number],
    trackers: ReactNode
  ) => {
    const selection = selectionById.get(instance.equipmentSelectionId);
    const choice = eligible.find(
      (item) => item.appliance.id === selection?.applianceId
    );
    const saved = selection?.specSnapshot;
    const allocation = solarInverterAllocationSummary(
      topology,
      panels,
      instance.id,
      excludedFaceIds
    );
    const product =
      typeof saved?.name === 'string'
        ? saved.name
        : (choice?.appliance.productSpec.name ?? 'Product unavailable');
    const unitSpec = choice?.appliance.productSpec;
    // Saved ratings first, then the live catalogue.
    const acKw = allocation.acOutputKw ?? unitSpec?.acOutputKw ?? null;
    const dcKw =
      allocation.allocatedDcKw ??
      (() => {
        const assigned = new Set(
          topology.strings
            .filter((item) => item.inverterInstanceId === instance.id)
            .flatMap((item) => item.panelIds)
        );
        let total = 0;
        for (const panel of panels) {
          if (!panel.included || !assigned.has(panel.id ?? '')) continue;
          const appliance = selectionById.get(
            panel.equipmentSelectionId ?? ''
          )?.applianceId;
          const watts = panelAppliances.find(
            (item) => item.appliance.id === appliance
          )?.appliance.productSpec.panelCapacityW;
          if (watts == null) return null;
          total += watts;
        }
        return total / 1000;
      })();
    const ratio =
      dcKw != null && acKw != null && acKw > 0
        ? ` (${(dcKw / acKw).toFixed(2)})`
        : '';
    const renaming = renamingUnit?.id === instance.id;
    const commitRename = () => {
      const value = renamingUnit?.value.trim();
      if (renaming && value && value !== instance.name)
        onChange({
          type: 'renameInverter',
          id: instance.id,
          name: value,
        });
      setRenamingUnit(null);
    };
    const changing = changingUnitId === instance.id;
    const ratings = [
      acKw != null ? `${acKw} kW AC` : null,
      `${dcKw != null ? dcKw.toFixed(1) : '?'} kW DC${ratio}`,
    ].filter(Boolean);
    const facts = [solarShortProductName(product), ...ratings].join(' · ');
    return (
      <div className="space-y-2 rounded-lg border border-gray-200 bg-white p-2">
        <div className="flex items-center gap-1">
          {renaming ? (
            <RawInput
              aria-label="Inverter instance name"
              className="w-32 shrink-0"
              maxLength={120}
              value={renamingUnit.value}
              onChange={(event) =>
                setRenamingUnit({
                  id: instance.id,
                  value: event.target.value,
                })
              }
              onKeyDown={(event) => {
                if (event.key === 'Enter') commitRename();
                if (event.key === 'Escape') setRenamingUnit(null);
              }}
              onBlur={commitRename}
            />
          ) : (
            <span className="shrink-0 text-sm font-medium">
              {instance.name}
            </span>
          )}
          <Tooltip
            className="min-w-0 flex-1"
            tooltipContent={[product, ...ratings].join(' · ')}
          >
            <span className="block truncate">
              <Text as="span" level="supporting">
                · {facts}
              </Text>
            </span>
          </Tooltip>
          <ContextMenuDropdown
            variant="quaternary"
            size="xs"
            label={`${instance.name} actions`}
            menuItems={[
              {
                label: 'Rename',
                onClick: () =>
                  setRenamingUnit({ id: instance.id, value: instance.name }),
              },
              {
                label: 'Change product',
                disabled: !eligible.some(
                  (item) => item.appliance.id !== selection?.applianceId
                ),
                onClick: () => setChangingUnitId(instance.id),
              },
              // Products without an MPPT count get one assumed input
              // automatically; another is declared only on request.
              ...(mpptCountOf(unitSpec) == null
                ? [
                    {
                      label: 'Add another input',
                      onClick: () => addAnotherInput(instance, unitSpec),
                    },
                  ]
                : []),
              {
                label: 'Remove',
                onClick: () =>
                  onChange({ type: 'removeInverter', id: instance.id }),
              },
            ]}
          />
        </div>
        {changing && (
          <Select
            multiple={false}
            aria-label={`Product for ${instance.name}`}
            className="w-full"
            value={selection?.applianceId ?? undefined}
            options={eligible.map(({ appliance }) => ({
              value: appliance.id,
              label: solarShortProductName(appliance.productSpec.name),
            }))}
            onChange={(value) => {
              setChangingUnitId(null);
              if (!value || value === selection?.applianceId) return;
              onChange({
                type: 'reviseInverter',
                id: instance.id,
                selectionId: crypto.randomUUID(),
                applianceId: value,
              });
              const spec = eligible.find((item) => item.appliance.id === value)
                ?.appliance.productSpec;
              for (const change of trackerDeclarations(instance.id, spec))
                onChange(change);
            }}
          />
        )}
        {trackers}
      </div>
    );
  };

  const stringBox = (string: SolarElectricalTopology['strings'][number]) => {
    const badge = stringBadge(string);
    const selected = selectedStringId === string.id;
    const roof = stringRoof(string.panelIds);
    const reason = badge.reasons.join(' · ');
    const badgeNode = (
      <Badge variant="soft" size="sm" colour={badge.colour}>
        {badge.label}
      </Badge>
    );
    return (
      <div
        key={string.id}
        role="listitem"
        onMouseEnter={() => onHoverPanels(string.panelIds)}
        onMouseLeave={() => onHoverPanels([])}
        onFocus={() => onHoverPanels(string.panelIds)}
        onBlur={() => onHoverPanels([])}
        className={`flex flex-wrap items-center gap-1 rounded-md border bg-white pr-1 ${
          selected
            ? 'border-primary outline outline-2 outline-primary'
            : 'border-gray-200'
        }`}
      >
        <button
          type="button"
          aria-pressed={selected}
          aria-label={[
            `${string.name} · ${plural(string.panelIds.length, 'panel', 'panels')}`,
            roof,
            badge.label,
            ...badge.reasons,
          ]
            .filter(Boolean)
            .join(' · ')}
          title={reason || undefined}
          className="flex min-w-0 grow basis-40 items-center gap-2 px-2 py-1 text-left"
          onClick={() =>
            selected
              ? selectString(null)
              : selectString(string.id, string.panelIds)
          }
        >
          <span
            aria-hidden
            data-testid="string-swatch"
            className="size-2.5 shrink-0 rounded-full"
            style={{
              backgroundColor: stringSwatch(colourKeys.get(string.id) ?? 0),
            }}
          />
          <span className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2">
            <span
              data-testid="string-name"
              className="break-words text-sm font-semibold"
            >
              {`${string.name} · ${plural(string.panelIds.length, 'panel', 'panels')}`}
            </span>
            {roof && (
              <span className="break-words">
                <Text as="span" level="supporting" data-testid="string-roof">
                  {roof}
                </Text>
              </span>
            )}
          </span>
        </button>
        {reason ? (
          <Tooltip tooltipContent={reason} className="ml-auto shrink-0">
            {badgeNode}
          </Tooltip>
        ) : (
          <span className="ml-auto shrink-0">{badgeNode}</span>
        )}
        {!string.inputKey && hasUnits && !selected && (
          <Button
            variant="quaternary"
            size="xs"
            className="shrink-0"
            aria-label={`Connect ${string.name}`}
            onClick={() => selectString(string.id, string.panelIds)}
          >
            Connect
          </Button>
        )}
      </div>
    );
  };
  const placed = new Set<string>();
  /** Each tracker as a plain list inside its unit's card, one string per row. */
  const trackerSections = (
    instance: SolarElectricalTopology['inverterInstances'][number]
  ) => {
    const spec = unitSpecOf(instance.equipmentSelectionId);
    return instance.trackers.map((tracker, trackerIndex) => {
      const own = topology.strings.filter(
        (item) =>
          item.inverterInstanceId === instance.id &&
          item.mpptKey === tracker.key &&
          tracker.inputs.some((input) => input.key === item.inputKey)
      );
      for (const item of own) placed.add(item.id);
      const label = solarTrackerLabel(
        spec,
        tracker.key,
        trackerIndex,
        instance.trackers.length
      );
      return (
        <div
          key={tracker.key}
          role="list"
          aria-label={`${instance.name} · ${label}`}
          className="space-y-1 border-t border-gray-100 pt-1.5"
        >
          <Text level="supporting">{label}</Text>
          {own.length ? (
            own.map(stringBox)
          ) : (
            <Text level="supporting">No string</Text>
          )}
        </div>
      );
    });
  };
  const tree = topology.inverterInstances.map((instance) => (
    <div key={instance.id} role="listitem" aria-label={instance.name}>
      {unitCard(instance, trackerSections(instance))}
    </div>
  ));
  const unassigned = topology.strings.filter((item) => !placed.has(item.id));

  const kwp = `${totalDcKw.toFixed(1)} kWp`;
  const activePanelCount = panels.filter(
    (item) => item.included && !excludedFaceIds.includes(item.faceId)
  ).length;
  const strungCount = topology.strings.filter(
    (item) => item.panelIds.length
  ).length;
  const unassignedCount = review.unassignedIncludedPanelCount;
  /** Per-roof notes for the (i): the shortfall itself is the line and the action. */
  const statusNotes = (autoSummary?.notes ?? []).filter(
    (note) => !/ no inverter input$/.test(note)
  );
  const showStatus = activePanelCount > 0 && (!!autoSummary || strungCount > 0);
  /**
   * The one action for panels without an input: the fleet's computed units
   * (or one more of the product in use), then a restring.
   */
  const fleetSized = !!fleet && fleet.unitsToAdd > 0;
  const unitsToAdd = fleetSized ? fleet.unitsToAdd : 1;
  const addProductId = fleetSized ? fleet.productId : applianceId;
  const showAddAction =
    hasUnits || autoSummary || strungCount > 0
      ? showStatus && unassignedCount > 0
      : fleetSized;
  const addProductName = eligible.find(
    (item) => item.appliance.id === addProductId
  )?.appliance.productSpec.name;
  let addActionNote = 'Choose a product under Inverters';
  if (fleetSized) addActionNote = `${kwp} needs ${fleet.note}`;
  else if (addProductId)
    addActionNote = `Adds ${
      addProductName ? solarShortProductName(addProductName) : 'a unit'
    } and restrings`;
  const addAction = showAddAction && (
    <Tooltip tooltipContent={addActionNote}>
      <Button
        variant="primary"
        className="w-full"
        onClick={() => {
          if (addProductId) {
            addUnits(addProductId, unitsToAdd, true);
            return;
          }
          const row = document.getElementById(ADD_UNIT_ROW_ID);
          row?.scrollIntoView?.({ block: 'nearest' });
          row?.querySelector<HTMLElement>('button, a')?.focus();
        }}
      >
        {unitsToAdd === 1
          ? 'Add inverter unit'
          : `Add ${unitsToAdd} inverter units`}
      </Button>
    </Tooltip>
  );

  const connectionLabel = selectedString?.inverterInstanceId
    ? [
        topology.inverterInstances.find(
          (item) => item.id === selectedString.inverterInstanceId
        )?.name,
        selectedString.mpptKey,
        selectedString.inputKey,
      ]
        .filter(Boolean)
        .join(' / ')
    : 'Not connected';
  const showConnection =
    !!selectedString &&
    (movingStringId === selectedString.id || !selectedString.inputKey);
  const editor = selectedString && (
    <section aria-label="Selected string" className="space-y-2">
      <div className="flex items-center gap-1">
        <span className="min-w-0 flex-1 break-words">
          <Text>
            {selectedString.name} ·{' '}
            {plural(selectedString.panelIds.length, 'panel', 'panels')} ·{' '}
            {connectionLabel}
          </Text>
        </span>
        {selectedString.inputKey && (
          <Button
            variant="quaternary"
            size="sm"
            aria-expanded={showConnection}
            onClick={() =>
              setMovingStringId(showConnection ? null : selectedString.id)
            }
          >
            Move…
          </Button>
        )}
        <ContextMenuDropdown
          variant="quaternary"
          size="xs"
          label={`${selectedString.name} actions`}
          menuItems={[
            {
              label: 'Rename',
              onClick: () => {
                setStringName(selectedString.name);
                setRenamingString(true);
              },
            },
            ...(selectedString.inverterInstanceId
              ? [
                  {
                    label: 'Disconnect',
                    onClick: () => {
                      setConnectionDraft(null);
                      onChange({
                        type: 'connectString',
                        id: selectedString.id,
                        inverterInstanceId: null,
                        mpptKey: null,
                        inputKey: null,
                      });
                    },
                  },
                ]
              : []),
            {
              label: `Delete ${selectedString.name}`,
              onClick: () => {
                onChange({ type: 'removeString', id: selectedString.id });
                selectString(null);
              },
            },
          ]}
        />
      </div>
      {showConnection && (
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <span className="w-20 shrink-0">
              <Text as="span" level="supporting">
                Inverter
              </Text>
            </span>
            <Select
              multiple={false}
              aria-label="Inverter for string"
              placeholder="Inverter"
              className="w-full"
              value={connectionInstanceId}
              options={topology.inverterInstances.map((item) => ({
                value: item.id,
                label: item.name,
              }))}
              onChange={(value) =>
                setConnectionDraft({
                  stringId: selectedString.id,
                  instanceId: value ?? '',
                  mpptKey: '',
                })
              }
            />
          </div>
          <div className="flex items-center gap-3">
            <span className="w-20 shrink-0">
              <Text as="span" level="supporting">
                MPPT
              </Text>
            </span>
            <Select
              multiple={false}
              aria-label="MPPT for string"
              placeholder="MPPT"
              className="w-full"
              disabled={!connectionInstance}
              value={connectionMpptKey}
              options={(connectionInstance?.trackers ?? []).map((item) => ({
                value: item.key,
                label: freeInputKey(connectionInstanceId, item.key)
                  ? item.key
                  : `${item.key} · full`,
              }))}
              onChange={(value) => {
                const mpptKey = value ?? '';
                setConnectionDraft({
                  stringId: selectedString.id,
                  instanceId: connectionInstanceId,
                  mpptKey,
                });
                const inputKey = freeInputKey(connectionInstanceId, mpptKey);
                if (inputKey)
                  onChange({
                    type: 'connectString',
                    id: selectedString.id,
                    inverterInstanceId: connectionInstanceId,
                    mpptKey,
                    inputKey,
                  });
              }}
            />
          </div>
          <div className="flex items-center gap-3">
            <span className="w-20 shrink-0">
              <Text as="span" level="supporting">
                DC input
              </Text>
            </span>
            <Select
              multiple={false}
              aria-label="Physical DC input for string"
              placeholder="Input"
              className="w-full"
              disabled={!connectionMpptKey}
              value={
                selectedString.inverterInstanceId === connectionInstanceId &&
                selectedString.mpptKey === connectionMpptKey
                  ? (selectedString.inputKey ?? '')
                  : ''
              }
              options={(
                connectionInstance?.trackers.find(
                  (item) => item.key === connectionMpptKey
                )?.inputs ?? []
              ).map((input) => {
                const occupied = topology.strings.some(
                  (item) =>
                    item.id !== selectedString.id &&
                    item.inverterInstanceId === connectionInstanceId &&
                    item.mpptKey === connectionMpptKey &&
                    item.inputKey === input.key
                );
                return {
                  value: input.key,
                  label: `${input.key}${occupied ? ' · already connected' : ''}`,
                  disabled: occupied,
                };
              })}
              onChange={(value) => {
                if (!value) return;
                setMovingStringId(null);
                onChange({
                  type: 'connectString',
                  id: selectedString.id,
                  inverterInstanceId: connectionInstanceId,
                  mpptKey: connectionMpptKey,
                  inputKey: value,
                });
              }}
            />
          </div>
        </div>
      )}
      {showConnection && needsAnotherInput && (
        <div className="flex items-center justify-between gap-2">
          <Text level="supporting">{connectionMpptKey} has no free input</Text>
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              onChange({
                type: 'declareInput',
                inverterInstanceId: connectionInstanceId,
                mpptKey: connectionMpptKey,
                inputKey: extraInputKey,
              })
            }
          >
            Declare another input
          </Button>
        </div>
      )}
      {(addingIds.length > 0 || removingIds.length > 0) && (
        <div className="flex flex-wrap gap-2">
          {addingIds.length > 0 && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                onChange({
                  type: 'assignPanels',
                  stringId: selectedString.id,
                  panelIds: addingIds,
                })
              }
            >
              Add {addingIds.length} selected panels to string
            </Button>
          )}
          {removingIds.length > 0 && (
            <Button
              variant="quaternary"
              size="sm"
              onClick={() =>
                onChange({
                  type: 'assignPanels',
                  stringId: null,
                  panelIds: removingIds,
                })
              }
            >
              Remove {removingIds.length} selected panels from string
            </Button>
          )}
        </div>
      )}
      {movingCount > 0 && (
        <Text level="supporting">
          {plural(movingCount, 'selected panel moves', 'selected panels move')}{' '}
          from other strings
        </Text>
      )}
      {renamingString && (
        <RawInput
          aria-label="String name"
          maxLength={120}
          value={stringName}
          onChange={(event) => setStringName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setRenamingString(false);
            if (event.key === 'Enter') event.currentTarget.blur();
          }}
          onBlur={() => {
            if (stringName.trim() && stringName !== selectedString.name)
              onChange({
                type: 'renameString',
                id: selectedString.id,
                name: stringName.trim(),
              });
            setRenamingString(false);
          }}
        />
      )}
    </section>
  );

  return (
    <div
      role="group"
      aria-label="Inverter instances and strings"
      className="space-y-4"
    >
      {layoutChangeDisabledReason && <Text>{layoutChangeDisabledReason}</Text>}
      {review.errors.length > 0 && (
        <div className="space-y-1">
          {review.errors.map((error) => (
            <Text key={error} level="supporting">
              {error}
            </Text>
          ))}
        </div>
      )}
      <section
        aria-labelledby="solar-inverter-units-title"
        className="space-y-2"
      >
        <H4 id="solar-inverter-units-title" tabIndex={-1}>
          Inverters
        </H4>
        {eligible.length ? (
          <div id={ADD_UNIT_ROW_ID} className="flex items-center gap-2">
            <Select
              multiple={false}
              aria-label="Standalone inverter product"
              className="min-w-0 flex-1"
              value={applianceId}
              options={eligible.map(({ appliance }) => ({
                value: appliance.id,
                label: solarShortProductName(appliance.productSpec.name),
              }))}
              onChange={(value) => setPickedApplianceId(value)}
            />
            <Button
              variant="secondary"
              size="sm"
              aria-label="Add selected inverter"
              disabled={!applianceId}
              onClick={addUnit}
            >
              Add
            </Button>
          </div>
        ) : (
          <div
            id={ADD_UNIT_ROW_ID}
            className="flex flex-wrap items-center justify-between gap-2"
          >
            <Text level="supporting">
              No standalone inverters in your catalogue
            </Text>
            <AnchorButton
              as={Link}
              to={estimateSettingsPathForCategory('INVERTER')}
              variant="link"
              size="sm"
            >
              Appliances settings
            </AnchorButton>
          </div>
        )}
      </section>
      <div className="space-y-1">
        <Tooltip enabled={!hasUnits} tooltipContent={NO_UNITS_TOOLTIP}>
          <Button
            variant="primary"
            className="w-full"
            disabled={!hasUnits}
            onClick={() => runAutoString()}
          >
            Auto-string
          </Button>
        </Tooltip>
        {showStatus && (
          <div
            role="status"
            aria-label="Auto-string summary"
            className="flex items-center gap-1"
          >
            <Text level="supporting">
              {[
                plural(strungCount, 'string', 'strings'),
                `${activePanelCount - unassignedCount} of ${activePanelCount} panels`,
                review.incompleteStringIds.length > 0 &&
                  `${review.incompleteStringIds.length} to review`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Text>
            {statusNotes.length > 0 && (
              <Tooltip tooltipContent={statusNotes.join(' · ')}>
                <span
                  role="img"
                  aria-label={statusNotes.join(' · ')}
                  tabIndex={0}
                  className="inline-flex text-gray-500"
                >
                  <UntitledIcon name="info-circle.3" className="size-4" />
                </span>
              </Tooltip>
            )}
          </div>
        )}
        {addAction}
      </div>
      <Divider />
      <section aria-labelledby="solar-strings-title" className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <H4 id="solar-strings-title">Strings</H4>
          <Tooltip enabled={!hasUnits} tooltipContent={NO_UNITS_TOOLTIP}>
            <Button
              variant="secondary"
              size="sm"
              disabled={!hasUnits}
              onClick={() =>
                onChange({
                  type: 'addString',
                  id: crypto.randomUUID(),
                  name: nextStringName(
                    topology.inverterInstances.map((item) => item.name),
                    topology.strings.map((item) => item.name)
                  ),
                })
              }
            >
              Add string
            </Button>
          </Tooltip>
        </div>
        {hasUnits && (
          <div role="list" aria-label="Inverter units" className="space-y-3">
            {tree}
          </div>
        )}
        {unassigned.length > 0 && (
          <div
            role="list"
            aria-label="Unassigned"
            className="space-y-1 rounded-md border border-dashed border-gray-300 p-1.5"
          >
            <Text level="supporting">Unassigned</Text>
            {unassigned.map(stringBox)}
          </div>
        )}
        {editor && (footer ? createPortal(editor, footer) : editor)}
      </section>
      <Divider />
      <ElectricalDiagram
        region={diagramRegion}
        topology={topology}
        panels={panels}
        faces={faces}
        excludedFaceIds={excludedFaceIds}
        stringColours={stringColours}
        appliances={appliances}
        panelAppliances={panelAppliances}
        battery={battery}
        siteMinTemperatureC={siteMinTemperatureC}
      />
    </div>
  );
};
